'use strict';
/* ==========================================================================
   levels/title.js — the title of "Who Eats Whom?" (food-web), botanical-plate collage (DESIGN.md §2): a field notebook
   lying open on aged paper, the marsh sketched along its pages; on the left page the apple snail's plate, on the right
   the snail kite's; an ink arrow draws itself FROM THE SNAIL TO THE KITE (the eater) and energy dots start to travel
   along it. The lesson name on kraft labels hung on garden twine (Plate.label woodtype), the subtitle hand-lettered,
   Lily (left) and Mr. Mendoza (right) rise in, ▶ Play → the map. On a phone held upright the controls move to the
   portrait dock (Dock.title). Levels.title.loading() draws the loading page (no textures yet: plain fills + ink).
   The arrow is the kit's (WebKit.arrowGeo / strokeArrow / dots) between the two plates: the same arrow the labs draw.
   Conventions: draw through g, a pure function of time (T, this.enteredAt), UI.button / UI.tap sizes, no shadowOn().
   ========================================================================== */
(function () {
  const PLAY = { x: 960, y: 984 };
  const PL = () => window.Plate, K = () => window.WebKit;
  const BOOK = { x: 960, y: 610, w: 1080, h: 520 };

  /* the title on 1–2 labels, balanced */
  function titleLines(text) {
    const words = String(text).toUpperCase().split(/\s+/).filter(Boolean);
    if (words.join(' ').length <= 16) return [words.join(' ')];
    let best = null;
    for (let i = 1; i < words.length; i++) { const L = [words.slice(0, i).join(' '), words.slice(i).join(' ')], m = Math.max(...L.map(s => s.length)); if (!best || m < best.m) best = { m, L }; }
    return best.L;
  }

  /* the open notebook: a kraft cover under two cream pages, a stitched spine, faint rules, the marsh sketched in pencil */
  function notebook(t0) {
    const P = PL(), C = P.C, B = BOOK, s = pop(T, t0 + 0.1, 0.45, 1.6);
    if (s <= 0) return;
    g.save(); g.translate(B.x, B.y); g.scale(s, s); g.translate(-B.x, -B.y);
    paperRect(B.x, B.y + 8, B.w + 40, B.h + 36, '#6e4f30', { seed: 401, depth: 1.6, boil: false, rot: -0.006 });
    for (const sx of [-1, 1]) paperRect(B.x + sx * B.w / 4, B.y, Math.round(B.w / 2 - 6), B.h, '#f4ead1', { seed: 402 + (sx > 0 ? 1 : 0), depth: 0.5, boil: false, rot: sx * 0.004 });
    g.save(); g.strokeStyle = 'rgba(31,95,107,0.14)'; g.lineWidth = 2;
    for (let y = B.y - B.h / 2 + 60; y < B.y + B.h / 2 - 20; y += 40) { g.beginPath(); g.moveTo(B.x - B.w / 2 + 30, y); g.lineTo(B.x - 20, y); g.moveTo(B.x + 20, y); g.lineTo(B.x + B.w / 2 - 30, y); g.stroke(); }
    g.restore();
    // the spine: a stitched fold
    g.save(); g.strokeStyle = 'rgba(58,42,28,0.45)'; g.lineWidth = 3; g.beginPath(); g.moveTo(B.x, B.y - B.h / 2 + 6); g.lineTo(B.x, B.y + B.h / 2 - 6); g.stroke();
    g.setLineDash([10, 12]); g.lineWidth = 2; g.strokeStyle = C.twine; g.beginPath(); g.moveTo(B.x + 5, B.y - B.h / 2 + 20); g.lineTo(B.x + 5, B.y + B.h / 2 - 20); g.stroke(); g.restore();
    // the marsh, sketched along the bottom of the pages: water lines and sawgrass blades (pencil)
    g.save(); g.strokeStyle = 'rgba(58,42,28,0.45)'; g.lineWidth = 2.5; g.lineCap = 'round';
    const yb = B.y + B.h / 2 - 40;
    for (let k = 0; k < 3; k++) { g.beginPath(); for (let x = B.x - B.w / 2 + 40; x <= B.x + B.w / 2 - 40; x += 20) { const y = yb + k * 12 + Math.sin(x * 0.03 + k) * 3; if (x === B.x - B.w / 2 + 40) g.moveTo(x, y); else g.lineTo(x, y); } g.stroke(); }
    for (let i = 0; i < 26; i++) { const x = B.x - B.w / 2 + 60 + i * 38 + (i % 3) * 7; if (Math.abs(x - B.x) < 30) continue; const h = 22 + (i * 37 % 20); g.beginPath(); g.moveTo(x, yb - 4); g.lineTo(x + 6 + (i % 2) * 5, yb - 4 - h); g.stroke(); }
    g.restore();
    g.restore();
  }

  class Title extends Scene {
    enter() { this.started = false; if (K()) K().load(); }
    go() {
      if (this.started) return;
      this.started = true;
      Audio.unlock();
      if (Game.MUSIC) Audio.music(Game.MUSIC, { gain: 0.3 });
      Game.replace(new Levels.map());
    }
    keyDown(k) { if (k === 'Enter' || k === ' ') this.go(); }

    draw() {
      UI.allLessons();                       // "← All lessons" (a paper tab; in the portrait dock a button in its bar)
      const t = this.t, t0 = this.enteredAt, L = Game.lesson || {}, P = PL(), C = P.C, Kt = K();
      P.backdrop(t0);
      notebook(t0);

      // the snail (left page) → the kite (right page): the arrow draws itself, then the energy travels to the eater
      if (Kt) {
        const ya = BOOK.y - 10, xa = BOOK.x - BOOK.w / 4, xb = BOOK.x + BOOK.w / 4;
        const A = Kt.plate('apple_snail', xa, ya + 30, { w: 230, t0: t0 + 0.5, rot: -0.03 });
        const B = Kt.plate('snail_kite', xb, ya - 30, { w: 230, t0: t0 + 0.7, rot: 0.025 });
        const arr = Kt.arrowGeo(A, B, { bias: 1 });
        const p = E.outCubic(c01((T - t0 - 1.3) / 0.9));
        if (p > 0) Kt.strokeArrow(arr, p, { w: 7 });
        if (p >= 1) Kt.dots([arr], { r: 10 });
        if (T > t0 + 2.3) Kt.text('the arrow points to the eater', BOOK.x, ya + 150, 40, { color: C.red, rot: -0.02, alpha: c01((T - t0 - 2.3) / 0.4) });
      }

      // the title: kraft labels on twine
      const lines = titleLines(L.title || 'WHO EATS WHOM?');
      const size = Math.min(86, ...lines.map(l => 1000 / (P.woodWidth(l, 100) / 100) - 8));
      const y0 = 150, dy = size * 1.95;
      P.twine(420, y0 - size * 1.3, 1500, y0 - size * 1.3, { sag: 24, seed: 71 });
      lines.forEach((l, i) => {
        const y = y0 + i * dy, s = pop(T, t0 + 0.3 + i * 0.3, 0.45, 1.8), sw = Math.sin(T * 1.2 + i * 1.9) * 0.008;
        P.label(l, 960, y, Math.round(size), { t0: t0 + 0.35 + i * 0.3, seed: 101 + i * 17, rot: [-0.012, 0.01][i % 2] + sw, s });
      });
      const ySub = y0 + (lines.length - 1) * dy + size * 1.4;
      paperRect(960, ySub + 4, 1180, 70, C.label, { seed: 91, depth: 0.9, boil: false, rot: -0.008, s: pop(T, t0 + 1.0, 0.35, 1.8) });
      hand(L.subtitle || '', 960, ySub + 4, 40, t0 + 1.1, { dur: 0.6, color: C.leafDark, rot: -0.008 });

      // Lily (left) and Mr. Mendoza (right) rise in from below
      const riseL = E.outBack(P_(t0 + 0.5, t0 + 1.0), 1.4), riseM = E.outBack(P_(t0 + 0.7, t0 + 1.2), 1.4);
      Puppet.draw('lily', 220, 1080 + (1 - riseL) * 600, 640, { facing: 1, t: T });
      Puppet.draw('mendoza', 1700, 1080 + (1 - riseM) * 600, 720, { facing: -1, t: T });

      if (UI.docked()) { Dock.title({ onStart: () => this.go(), onContinue: () => this.go() }); return; }
      if (t > 1.4 && UI.button('play', '▶ Play', PLAY.x, PLAY.y, { w: 380, h: 112, color: C.leafDark, font: "'Luckiest Guy'", size: 62, rot: -0.015 })) this.go();
      if (t > 2.4) hand(UI.hint('(or press Enter)', 'tap ▶ Play to start'), PLAY.x + 215, PLAY.y + 6, UI.text(30), t0 + 2.4, { color: 'rgba(43,36,32,0.75)', seed: 61, align: 'left' });   // just right of the 380-wide button
    }
  }
  const P_ = (a, b) => P(T, a, b);
  const c01 = x => Math.max(0, Math.min(1, x));
  Title.PLAY = PLAY;                         // tools/integration.js clicks it

  /* loading screen: plain paper, an ink arrow drawing itself from one dot to the next, over and over */
  Title.loading = function () {
    g.fillStyle = '#efe4c8'; g.fillRect(0, 0, Game.W, Game.H);
    const u = (T * 0.8) % 1.4;
    g.save(); g.fillStyle = '#5b8a3c'; g.beginPath(); g.arc(720, 540, 26, 0, TAU); g.fill(); g.fillStyle = '#8a3b1e'; g.beginPath(); g.arc(1200, 540, 26, 0, TAU); g.fill(); g.restore();
    ink([[760, 540], [760 + 380 * Math.min(1, u), 540]], { w: 8, color: '#3a2a1c', seed: 7, passes: 1, step: 40 });
    if (u > 1) ink([[1158, 520], [1168, 540], [1158, 560]], { w: 8, color: '#3a2a1c', seed: 8, passes: 1, step: 8 });
    hand('following the arrows…', 960, 660, 60, 0, { dur: 0.01, seed: 5, color: '#2b2420' });
  };

  Levels.title = Title;
})();
